import { useEffect, useMemo } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import HardBreak from '@tiptap/extension-hard-break'
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { toEditableHtml, fromEditorHtml } from './richTextConversion.js'
import { PREVIEW_PARAGRAPH_SX } from './templatePreview.js'

// A custom hard-break node carrying a `tight` attribute — lets an admin
// insert a genuinely tight (no blank-line) line break within a template,
// distinct from Enter's own new-paragraph-with-1em-gap behavior. Only a
// NEW break inserted via Shift/Mod-Enter (or the toolbar button below) is
// ever marked tight: true; an existing bare <br> loaded from stored content
// parses as tight: false (parseHTML only looks for the data-tight
// attribute) and re-serializes back to a bare <br>, byte-identical to
// today, even if the admin saves an unrelated edit elsewhere in the same
// template. Since 2026-10-28 every <br>, tight or not, is one line break in
// the template's text and preview (@cxhub/shared's templateHtmlToPlainText(),
// templatePreview.js's previewHtml()), as this editor shows it.
const TightHardBreak = HardBreak.extend({
  addAttributes() {
    return {
      tight: {
        default: false,
        parseHTML: (element) => element.hasAttribute('data-tight'),
        renderHTML: (attributes) => (attributes.tight ? { 'data-tight': 'true' } : {}),
      },
    }
  },
  addKeyboardShortcuts() {
    return {
      'Shift-Enter': () => this.editor.commands.insertContent({ type: this.name, attrs: { tight: true } }),
      'Mod-Enter': () => this.editor.commands.insertContent({ type: this.name, attrs: { tight: true } }),
    }
  },
})

// Rich-text template body editor (TemplatesPage.jsx's admin editor) — an
// admin should never have to look at literal <p>/<strong> tags to edit a
// template. A separate component from RichReplyEditor.jsx (not a reuse of
// it) because the toolbar needs is different — templates are authored, not
// drafted turn-by-turn, so they need list/underline support a one-off reply
// box doesn't — but it's built on the exact same TipTap/StarterKit + Link
// foundation, and the SAME toEditableHtml()/fromEditorHtml() round-trip
// (richTextConversion.js) for loading/saving, for the same critical reason:
// eBay templates are stored and sent as plain text with no HTML tags at all
// (see import-ebay-templates.js) — nothing strips HTML before that send —
// while 1A Website templates ARE real HTML (import-1a-website-templates.js).
// If this editor always serialized to wrapped HTML on save, opening and
// re-saving an eBay template with no edits would corrupt it into showing
// literal <p> tags to a real buyer. fromEditorHtml() flattens content with
// no real formatting beyond trivial p/br straight back to the exact plain
// string, so an eBay template round-trips byte-identical; only genuine
// formatting (bold/italic/underline/lists/links) is kept as HTML.
export default function TemplateRichEditor({ label, value, onChange, disabled, minRows = 10 }) {
  const initialContent = useMemo(() => toEditableHtml(value), []) // eslint-disable-line react-hooks/exhaustive-deps

  const editor = useEditor({
    extensions: [StarterKit.configure({ hardBreak: false }), TightHardBreak, Link.configure({ openOnClick: false, autolink: true }), Underline],
    content: initialContent,
    editable: !disabled,
    onUpdate: ({ editor: e }) => onChange(fromEditorHtml(e.getHTML())),
    editorProps: { attributes: { 'aria-label': label ?? '', role: 'textbox' } },
  }, [label])

  // Same external-value resync as RichReplyEditor.jsx (e.g. switching which
  // template is open in the same dialog instance) — only reset when the
  // incoming value doesn't match what this editor would itself produce right
  // now, and pass the options-object form of setContent's second arg (TipTap
  // v3), not a bare boolean — see RichReplyEditor.jsx's own comment for the
  // exact bug that shape mismatch caused.
  useEffect(() => {
    if (!editor) return
    const current = fromEditorHtml(editor.getHTML())
    if (current !== (value ?? '')) editor.commands.setContent(toEditableHtml(value), { emitUpdate: false })
  }, [editor, value])

  useEffect(() => {
    editor?.setEditable(!disabled, false)
  }, [editor, disabled])

  if (!editor) return null

  function ToolbarButton({ title, active, onClick, children }) {
    return (
      <Tooltip title={title}>
        <span>
          <IconButton size="small" disabled={disabled} color={active ? 'primary' : 'default'} onClick={onClick}>
            {children}
          </IconButton>
        </span>
      </Tooltip>
    )
  }

  return (
    <Stack sx={{ flex: 1, alignSelf: 'stretch', minWidth: 0 }}>
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25 }}>
          {label}
        </Typography>
      )}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 0.75 }}>
        <Stack direction="row" spacing={0.5} sx={{ mb: 0.25 }}>
          <ToolbarButton title="Bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
            <Typography variant="body2" fontWeight={700} sx={{ width: 16, textAlign: 'center' }}>B</Typography>
          </ToolbarButton>
          <ToolbarButton title="Italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
            <Typography variant="body2" fontStyle="italic" sx={{ width: 16, textAlign: 'center' }}>I</Typography>
          </ToolbarButton>
          <ToolbarButton title="Underline" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}>
            <Typography variant="body2" sx={{ width: 16, textAlign: 'center', textDecoration: 'underline' }}>U</Typography>
          </ToolbarButton>
          <ToolbarButton title="Bulleted list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
            <Typography variant="body2" sx={{ width: 16, textAlign: 'center' }}>•≡</Typography>
          </ToolbarButton>
          <ToolbarButton title="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
            <Typography variant="body2" sx={{ width: 16, textAlign: 'center', fontSize: '0.7rem' }}>1.≡</Typography>
          </ToolbarButton>
          <ToolbarButton title="Tight line break (no blank line) — Shift+Enter" onClick={() => editor.chain().focus().insertContent({ type: 'hardBreak', attrs: { tight: true } }).run()}>
            <Typography variant="body2" sx={{ width: 16, textAlign: 'center', fontSize: '0.7rem' }}>↵</Typography>
          </ToolbarButton>
          <ToolbarButton
            title="Link"
            active={editor.isActive('link')}
            onClick={() => {
              const previousUrl = editor.getAttributes('link').href ?? ''
              const url = window.prompt('Link URL', previousUrl)
              if (url === null) return
              if (!url) { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return }
              editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
            }}
          >
            <Typography variant="body2" sx={{ width: 16, textAlign: 'center', textDecoration: 'underline' }}>L</Typography>
          </ToolbarButton>
        </Stack>
        <Box
          sx={{
            minHeight: `${minRows * 1.5}em`,
            maxHeight: '50vh',
            overflowY: 'auto',
            '& .ProseMirror': { outline: 'none' },
            // The same spacing as the template previews (templatePreview.js).
            ...PREVIEW_PARAGRAPH_SX,
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <EditorContent editor={editor} />
        </Box>
      </Box>
    </Stack>
  )
}
